import { decryptAES, encryptAES } from './crypto/AES';
import { readMigratedItem, writeMigratedItem } from './crypto/storageMigration';
import { STORAGE_KEY } from './storageKeys';
import { WindowDispatchEvent } from './WindowDispatchEvent';

export interface FavoriteItem {
  id: number;
  title: string;
  url: string;
  imageUrl: string;
}

const isBrowser = () => typeof window !== 'undefined';

const safeLocalStorageGet = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSet = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore quota / security errors
  }
};

const safeLocalStorageRemove = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const favStore = {
  getItem: safeLocalStorageGet,
  setItem: safeLocalStorageSet,
  removeItem: safeLocalStorageRemove,
};

export const getFavorites = (): FavoriteItem[] => {
  if (!isBrowser()) return [];

  try {
    const favValue = readMigratedItem(favStore, STORAGE_KEY.FAVORITES);
    if (favValue) {
      const decrypted = decryptAES(favValue);
      const parsed = JSON.parse(decrypted);
      // Migration: handle old format (number[]) gracefully
      if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'number') {
        return [];
      }
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (e) {
    // decrypt / JSON parse errors
  }

  return [];
};

export const setFavorites = (items: FavoriteItem[]) => {
  if (!isBrowser()) return;
  const current = getFavorites();

  if (JSON.stringify(current) === JSON.stringify(items)) {
    return;
  }

  const encrypted = encryptAES(JSON.stringify(items));
  writeMigratedItem(favStore, STORAGE_KEY.FAVORITES, encrypted);

  window.dispatchEvent(new Event(WindowDispatchEvent.UPDATE_FAVORITES));
};

export const getFavoriteIds = (): number[] => {
  return getFavorites().map((item) => item.id);
};

export const isFavorite = (productId: number): boolean => {
  return getFavoriteIds().includes(productId);
};

export const addFavorite = (item: FavoriteItem) => {
  const current = getFavorites();
  if (!current.some((f) => f.id === item.id)) {
    setFavorites([...current, item]);
  }
};

export const removeFavorite = (productId: number) => {
  const current = getFavorites();
  setFavorites(current.filter((f) => f.id !== productId));
};

export const clearFavorites = () => {
  setFavorites([]);
};

export const getFavoritesCount = (): number => {
  return getFavorites().length;
};
